"use client"

import { useCallback } from "react"
import usePromotionEventStore, {
  EventActionType,
} from "../store/usePromotionEventStore"
import {
  useGlobalPopupStore,
  type PopupOpenOptions,
} from "@/components/common/popup/store/useGlobalPopupStore"
import { PROMOTION_EVENT_POPUP_CONFIG } from "../EventConfig"

/**
 * 프로모션 이벤트를 처리하는 커스텀 훅
 * 액션을 입력받아 해당 액션을 포함하는 모든 활성 이벤트를 자동으로 처리합니다.
 *
 * @example
 * const { checkEvent } = usePromotionEvent()
 * checkEvent('book')     // 책 등록 완료 시 - book 액션을 가진 모든 이벤트 처리
 * checkEvent('sentence') // 문장 등록 완료 시 - sentence 액션을 가진 모든 이벤트 처리
 */
const usePromotionEvent = () => {
  const {
    rlEvents,
    getEventsWithAction,
    removeEvent,
    markEventAction,
    clearExpiredEvents,
  } = usePromotionEventStore()
  const { openPopup, closePopup } = useGlobalPopupStore()

  const showPopup = useCallback(
    (
      eventCode: string,
      options?: PopupOpenOptions,
      action?: EventActionType,
    ) => {
      const config = PROMOTION_EVENT_POPUP_CONFIG[eventCode]
      if (!config) return

      const PopupComponent = config.component
      const isEach = config.actionsMode === "each"

      // "each" 모드: 액션별 문구/옵션은 컴포넌트에서 결정,
      // 훅은 어떤 액션이 완료됐는지(action)만 전달한다.
      openPopup(
        <PopupComponent
          action={isEach ? action : undefined}
          confirmLabel={
            isEach && action === "recommendation"
              ? "이벤트로 이동"
              : undefined
          }
          navigateToEvent={isEach && action === "recommendation"}
          onClose={() => closePopup()}
        />,
        options,
      )
    },
    [openPopup, closePopup],
  )

  /**
   * 액션 기반 이벤트 처리
   * 해당 액션을 포함하는 모든 활성 이벤트를 찾아 처리합니다.
   *
   * actionsMode별 동작:
   * - "or"/"and": 완료 조건 충족 시 팝업 표시 후 제거
   * - "each":     각 액션 완료 시 개별 팝업 (dedup으로 각 1회), 전체 완료 시에만 제거
   *
   * @param action - 완료된 액션 타입 (예: "book", "sentence")
   * @param options - 팝업 옵션 (예: { mode: "queue" })
   */
  const checkEvent = useCallback(
    (action: EventActionType, options?: PopupOpenOptions) => {
      clearExpiredEvents()

      const eventsWithAction = getEventsWithAction(action)
      if (eventsWithAction.length === 0) return

      const { progresses } = usePromotionEventStore.getState()

      // 각 이벤트에 대해 액션 처리
      for (const eventCode of eventsWithAction) {
        const config = PROMOTION_EVENT_POPUP_CONFIG[eventCode]
        const isEach = config?.actionsMode === "each"

        // "each" 모드: 이미 완료된 액션은 skip (최초 1회만 팝업)
        if (isEach && progresses[eventCode]?.[action] === true) continue

        // 액션 완료 표시
        markEventAction(eventCode, action)

        // 모든 액션이 완료되었는지 확인
        const complete = usePromotionEventStore
          .getState()
          .isEventComplete(eventCode)

        if (isEach) {
          // "each": 액션 완료 시 개별 팝업 (순서 무관, 각 액션별 1회)
          // 전체 완료 시에만 이벤트 제거 (다음 진입 시 재노출 방지)
          showPopup(eventCode, options, action)
          if (complete) removeEvent(eventCode)
        } else if (complete) {
          // "or"/"and": 완료 조건 충족 시에만 팝업 + 제거
          showPopup(eventCode, options)
          removeEvent(eventCode)
        }
      }
    },
    [
      getEventsWithAction,
      markEventAction,
      showPopup,
      clearExpiredEvents,
      removeEvent,
    ],
  )

  return {
    checkEvent,
    currentEvents: rlEvents,
    hasAnyEvent: rlEvents.length > 0,
  }
}

export default usePromotionEvent
